import { baseApi } from "./baseApi";

export interface CardMarketSummary {
  provider?: string | null;
  variant?: string | null;
  source?: string | null;
  sourceUrl?: string | null;
  currency?: string | null;
  low?: number | null;
  mid?: number | null;
  high?: number | null;
  market?: number | null;
  fetchedAt?: string | null;
}

export interface CardMarketResponse {
  card: CardSearchItem;
  market: CardMarketSummary & {
    series?: number[];
  };
}

export interface CardSearchItem {
  game: string;
  provider?: string;
  cardId: string;
  providerSetId?: string;
  cardName: string;
  setName?: string;
  cardNumber?: string;
  normalizedCardNumber?: string;
  rarity?: string;
  language?: string;
  finish?: string;
  layout?: string;
  releaseDate?: string | null;
  imageUrl?: string;
  imageUrlSmall?: string;
  imageUrlLarge?: string;
  imageUrlBack?: string;
  market?: CardMarketSummary | null;
}

export interface CardScanAuthenticityCheck {
  name: string;
  label: string;
  status: string;
  confidence?: number | null;
  summary?: string;
  reasons?: string[];
}

export interface CardScanComparisonCandidate extends CardSearchItem {
  confidence?: number;
  candidateConfidence?: number;
  authenticityConfidence?: number | null;
  authenticityDecision?: string;
  mismatchCount?: number;
  comparisonSummary?: string;
  supportingEvidence?: string[];
  contradictions?: string[];
  checks?: CardScanAuthenticityCheck[];
  missingEvidence?: string[];
}

export interface CardScanJob {
  id: string;
  flowContext: string;
  status: string;
  provider?: string | null;
  providerModel?: string | null;
  progress: number;
  frontImageUrl?: string;
  backImageUrl?: string;
  errorMessage?: string | null;
  result?: {
    candidates?: CardScanComparisonCandidate[];
    selectedCandidate?: (CardSearchItem & { confidence?: number; authenticityConfidence?: number | null }) | null;
    analysis?: {
      recognition?: {
        confidence?: number | null;
        clues?: Record<string, unknown>;
      };
      catalogComparison?: {
        bestCandidateCardId?: string | null;
        candidates?: CardScanComparisonCandidate[];
      };
      authenticityDecision?: string;
      authenticityBlockReason?: string | null;
      authenticityConfidence?: number | null;
      authenticityChecks?: CardScanAuthenticityCheck[];
      missingEvidence?: string[];
      recommendedAction?: string;
      confirmationAllowed?: boolean;
      explanation?: string | null;
      detectedClues?: Record<string, unknown>;
      confidence?: number | null;
      conditionEstimate?: { label?: string | null; confidence?: number | null; reasons?: string[] };
      likelyGradeRange?: { min?: number | null; max?: number | null; confidence?: number | null };
      authenticityRisk?: { level?: string | null; confidence?: number | null; reasons?: string[] };
      imageIntegrity?: { level?: string | null; confidence?: number | null; reasons?: string[] };
      notes?: string[];
      [key: string]: unknown;
    };
    warnings?: string[];
    requiresConfirmation?: boolean;
    confirmationAllowed?: boolean;
  };
  createdAt?: string;
  startedAt?: string | null;
  completedAt?: string | null;
}

export interface CollectionItem {
  id: string;
  status: string;
  quantity: number;
  cardGame: string;
  cardId: string;
  cardNameCanonical: string;
  displayName?: string;
  condition?: string | null;
  gradingCompany?: string | null;
  gradeNumber?: string | null;
  frontImageUrl?: string | null;
  backImageUrl?: string | null;
  scanAnalysis?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string | null;
}

export interface CollectionItemsResponse {
  readyToSave: CollectionItem[];
  saved: CollectionItem[];
}

export interface CollectionBulkSaveResult {
  id: string;
  outcome: "saved" | "merged_duplicate" | "skipped_duplicate" | "failed";
  item?: CollectionItem | null;
  error?: string | null;
}

export interface CollectionBulkSaveResponse {
  results: CollectionBulkSaveResult[];
  savedCount: number;
  mergedDuplicateCount: number;
  skippedDuplicateCount: number;
  failedCount: number;
}

export interface CollectionBulkDiscardResult {
  id: string;
  outcome: "discarded" | "failed";
  item?: CollectionItem | null;
  error?: string | null;
}

export interface CollectionBulkDiscardResponse {
  results: CollectionBulkDiscardResult[];
  discardedCount: number;
  failedCount: number;
}

export const cardsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    searchCards: builder.query<CardSearchItem[], { game: string; q: string; limit?: number }>({
      query: ({ game, q, limit = 10 }) => ({
        url: "/cards/search",
        method: "GET",
        params: { game, q, limit },
        usePublic: true,
      }),
      providesTags: (_res, _err, arg) => [{ type: "Cards", id: `${arg.game}:${arg.q}` }],
    }),
    resolveCard: builder.mutation<CardSearchItem, { game: string; queryText: string }>({
      query: (payload) => ({
        url: "/cards/resolve",
        method: "POST",
        data: payload,
      }),
    }),
    getCardMarket: builder.query<CardMarketResponse, { game: string; cardId: string }>({
      query: ({ game, cardId }) => ({
        url: `/cards/${encodeURIComponent(game)}/${encodeURIComponent(cardId)}/market`,
        method: "GET",
        usePublic: true,
      }),
      providesTags: (_res, _err, arg) => [{ type: "Cards", id: `market:${arg.game}:${arg.cardId}` }],
    }),
    getCardSoldStats: builder.query<any, { game: string; cardId: string }>({
      query: ({ game, cardId }) => ({
        url: `/cards/${encodeURIComponent(game)}/${encodeURIComponent(cardId)}/sold-stats`,
        method: "GET",
        usePublic: true,
      }),
      providesTags: (_res, _err, arg) => [{ type: "Cards", id: `sold:${arg.game}:${arg.cardId}` }],
    }),
    createCardScanJob: builder.mutation<{ id: string; status: string; progress: number }, FormData>({
      query: (payload) => ({
        url: "/cards/scan-jobs",
        method: "POST",
        data: payload,
        headers: { "Content-Type": "multipart/form-data" },
      }),
    }),
    getCardScanJob: builder.query<CardScanJob, { id: string }>({
      query: ({ id }) => ({
        url: `/cards/scan-jobs/${encodeURIComponent(id)}`,
        method: "GET",
      }),
      providesTags: (_res, _err, arg) => [{ type: "Cards", id: `scan:${arg.id}` }],
    }),
    confirmCardScanJob: builder.mutation<
      {
        confirmedCard: CardSearchItem | null;
        collectionItem?: CollectionItem | null;
        scanResult?: CardScanJob["result"];
      },
      { id: string; payload: Record<string, unknown> }
    >({
      query: ({ id, payload }) => ({
        url: `/cards/scan-jobs/${encodeURIComponent(id)}/confirm`,
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Cards"],
    }),
    getCollectionItems: builder.query<CollectionItemsResponse, void>({
      query: () => ({
        url: "/cards/collection",
        method: "GET",
      }),
      providesTags: ["Cards"],
    }),
    saveCollectionItem: builder.mutation<CollectionItem, { id: string }>({
      query: ({ id }) => ({
        url: `/cards/collection/${encodeURIComponent(id)}/save`,
        method: "POST",
      }),
      invalidatesTags: ["Cards"],
    }),
    saveCollectionItemsBulk: builder.mutation<CollectionBulkSaveResponse, { ids: string[] }>({
      query: (payload) => ({
        url: "/cards/collection/save-bulk",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Cards"],
    }),
    discardCollectionItemsBulk: builder.mutation<CollectionBulkDiscardResponse, { ids: string[] }>({
      query: (payload) => ({
        url: "/cards/collection/discard-bulk",
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["Cards"],
    }),
  }),
});

export const {
  useSearchCardsQuery,
  useResolveCardMutation,
  useGetCardMarketQuery,
  useGetCardSoldStatsQuery,
  useCreateCardScanJobMutation,
  useGetCardScanJobQuery,
  useConfirmCardScanJobMutation,
  useGetCollectionItemsQuery,
  useSaveCollectionItemMutation,
  useSaveCollectionItemsBulkMutation,
  useDiscardCollectionItemsBulkMutation,
} = cardsApi;
